import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MessagesSquare, Search, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../../components/ui/alert-dialog";
import { SegChat } from "../../components/SegChat";

export const SEG_MSG_EVENT = "pgp-seg-msg-changed";

export default function MessaggiSegnalatori() {
  const [threads, setThreads] = useState([]);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [q, setQ] = useState("");
  const [soloConv, setSoloConv] = useState(true);
  const [confirm, setConfirm] = useState(null); // { all: true } | { msg }

  const loadThreads = useCallback(() => {
    api.get("/studio/seg-messaggi/threads").then(({ data }) => setThreads(data || [])).catch(() => {});
  }, []);

  const loadMsgs = useCallback((id, silent) => {
    if (!silent) setLoadingMsgs(true);
    api.get(`/studio/seg-messaggi/${id}`)
      .then(({ data }) => { setMsgs(data || []); window.dispatchEvent(new Event(SEG_MSG_EVENT)); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoadingMsgs(false));
  }, []);

  useEffect(() => {
    loadThreads();
    const id = setInterval(loadThreads, 30000);
    return () => clearInterval(id);
  }, [loadThreads]);

  useEffect(() => {
    if (!sel) return;
    loadMsgs(sel.segnalatore_id);
    const id = setInterval(() => loadMsgs(sel.segnalatore_id, true), 20000);
    return () => clearInterval(id);
  }, [sel, loadMsgs]);

  const send = async (text) => {
    try {
      const { data } = await api.post(`/studio/seg-messaggi/${sel.segnalatore_id}`, { text });
      setMsgs((m) => [...m, data]);
      loadThreads();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; }
  };

  const doDelete = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      if (c.all) {
        await api.delete(`/studio/seg-messaggi/${sel.segnalatore_id}`);
        setMsgs([]);
        toast.success("Conversazione eliminata");
      } else {
        await api.delete(`/studio/seg-messaggi/${sel.segnalatore_id}/messaggio/${c.msg.id}`);
        setMsgs((m) => m.filter((x) => x.id !== c.msg.id));
        toast.success("Messaggio eliminato");
      }
      loadThreads();
      window.dispatchEvent(new Event(SEG_MSG_EVENT));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const visible = threads.filter((t) => (!soloConv || t.last_at || t.segnalatore_id === sel?.segnalatore_id)
    && (!q || t.nominativo.toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="p-4 sm:p-6" data-testid="messaggi-segnalatori-page">
      <h1 className="font-head text-2xl font-bold text-slate-100 flex items-center gap-2 mb-4"><MessagesSquare className="h-6 w-6 text-amber-400" /> Messaggi segnalatori</h1>
      <div className="grid md:grid-cols-[300px_1fr] gap-4 h-[calc(100vh-170px)] min-h-[420px]">
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 flex flex-col overflow-hidden">
          <div className="p-2 border-b border-slate-800 space-y-2">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca segnalatore…" data-testid="segmsg-search"
                className="w-full h-9 pl-8 pr-2 rounded-md border border-slate-700 bg-slate-900/60 text-sm text-slate-100 focus:outline-none focus:border-amber-500" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
              <input type="checkbox" checked={soloConv} onChange={(e) => setSoloConv(e.target.checked)} data-testid="segmsg-solo-conv" /> Solo con conversazioni
            </label>
          </div>
          <div className="flex-1 overflow-y-auto">
            {visible.length === 0 && <div className="text-center text-xs text-slate-500 py-8" data-testid="segmsg-threads-empty">Nessuna conversazione.</div>}
            {visible.map((t) => (
              <button key={t.segnalatore_id} type="button" onClick={() => setSel(t)} data-testid={`segmsg-thread-${t.segnalatore_id}`}
                className={`w-full text-left px-3 py-2.5 border-b border-slate-800/70 transition-colors ${sel?.segnalatore_id === t.segnalatore_id ? "bg-amber-500/10" : "hover:bg-slate-800/50"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-100 truncate">{t.nominativo}</span>
                  {t.unread > 0 && <span className="text-[10px] font-mono bg-amber-500 text-[#0a0d14] rounded-full px-1.5 py-0.5 font-semibold" data-testid={`segmsg-unread-${t.segnalatore_id}`}>{t.unread}</span>}
                </div>
                <div className="text-xs text-slate-400 truncate mt-0.5">{t.last ? `${t.last_from === "studio" ? "Tu: " : ""}${t.last}` : "Nessun messaggio"}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 flex flex-col overflow-hidden">
          {!sel ? <div className="flex-1 flex items-center justify-center text-sm text-slate-500" data-testid="segmsg-no-selection">Seleziona un segnalatore per vedere la conversazione.</div> : (
            <>
              <div className="px-4 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-100" data-testid="segmsg-chat-title">{sel.nominativo}</div>
                  {sel.email && <div className="text-xs text-slate-500">{sel.email}</div>}
                </div>
                {msgs.length > 0 && (
                  <button type="button" onClick={() => setConfirm({ all: true })} data-testid="segmsg-delete-all"
                    className="h-8 px-3 rounded-md border border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs font-semibold inline-flex items-center gap-1.5 shrink-0">
                    <Trash2 className="h-3.5 w-3.5" /> Elimina tutti
                  </button>
                )}
              </div>
              <div className="flex-1 min-h-0"><SegChat messages={msgs} mine="studio" onSend={send} onDelete={(m) => setConfirm({ msg: m })} loading={loadingMsgs} testPrefix="segmsg-chat" /></div>
            </>
          )}
        </div>
      </div>
      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent className="bg-[#121824] border-slate-700 text-slate-100" data-testid="segmsg-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.all ? `Eliminare tutti i messaggi con ${sel?.nominativo || ""}?` : "Eliminare questo messaggio?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirm?.all ? `Verranno eliminati definitivamente ${msgs.length} messaggi, anche dall'area riservata del segnalatore.` : `«${(confirm?.msg?.text || "").slice(0, 120)}» verrà eliminato definitivamente, anche dall'area riservata del segnalatore.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="segmsg-delete-cancel" className="border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="segmsg-delete-confirm-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
